
/**
 * Version Management System
 * 
 * This file serves as the single source of truth for application versioning.
 * It provides utilities for version tracking and comparison to prevent caching issues.
 */

// Import the mobile device info function from cacheVerification
import { getMobileDeviceInfo } from './cacheVerification';

// Get build ID from environment or generate a fallback using timestamp for uniqueness
export const BUILD_ID = import.meta.env.VITE_BUILD_ID || 
  `${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Date.now().toString().slice(-6)}`;

// Format: YYYYMMDD-COUNTER (counter is added by the build system or timestamp)
export const APP_VERSION = BUILD_ID;

// Add version parameter to URLs (only for dynamic resources)
export const addVersionToUrl = (url: string): string => {
  try {
    if (!url || 
        url.startsWith('data:') ||
        url.includes('_v=') || 
        url.includes('?v=') ||
        !url.startsWith('/')) {
      return url;
    }
    
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_v=${APP_VERSION}-${Date.now().toString().slice(-6)}`;
  } catch (e) {
    return url;
  }
};

// Check if current URL has cache busting parameters
export const hasVersionParam = (): boolean => {
  return window.location.href.includes('_v=') || 
         window.location.href.includes('_cache=');
};

// Clean URL by removing version parameters
export const cleanVersionParams = (url: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('_v');
    urlObj.searchParams.delete('_cache');
    return urlObj.toString();
  } catch (e) {
    // Fallback for older browsers
    return url.replace(/[\?&]_v=[^&]+/, '')
              .replace(/[\?&]_cache=[^&]+/, '');
  }
};

// Reduced reload cooldown from 5 minutes to 10 seconds for development and 1 hour for production
const RELOAD_COOLDOWN = import.meta.env.DEV ? 10000 : 3600000; // 10 seconds in dev, 1 hour in production

interface VersionInfo {
  version: string;
  lastReload: number;
  buildId: string;
}

export const initVersionControl = (): void => {
  try {
    // Skip version control check for the first 5 seconds after page load
    // This prevents immediate reloads in development
    const pageLoadTime = window.performance.timing.navigationStart || Date.now() - 5000;
    if (Date.now() - pageLoadTime < 5000) {
      console.log('Skipping version check during initial page load');
      return;
    }

    // Get stored version info
    const storedInfo = localStorage.getItem('version_info');
    const currentTime = Date.now();
    
    // Current version info
    const currentInfo: VersionInfo = {
      version: APP_VERSION,
      lastReload: currentTime,
      buildId: BUILD_ID
    };

    // Check if version.json exists and has updated info
    checkRemoteVersion()
      .then(remoteInfo => {
        if (remoteInfo && remoteInfo.forceRefresh === true) {
          // Only force refresh if explicitly set to true (strict comparison)
          console.log('Force refresh requested from server');
          performReload(currentInfo);
          return;
        }
        
        // Skip the rest if no stored info (first visit)
        if (!storedInfo) {
          localStorage.setItem('version_info', JSON.stringify(currentInfo));
          return;
        }

        const parsedInfo = JSON.parse(storedInfo) as VersionInfo;
        
        // Check if versions are different
        const versionChanged = parsedInfo.version !== APP_VERSION || 
                              parsedInfo.buildId !== BUILD_ID;
                              
        // Check if enough time has passed since last reload
        const timeSinceReload = currentTime - parsedInfo.lastReload;
        
        if (versionChanged && timeSinceReload > RELOAD_COOLDOWN) {
          console.log('Version changed and cooldown passed, reloading');
          performReload(currentInfo);
        } else {
          // Update stored version without reloading
          localStorage.setItem('version_info', JSON.stringify(currentInfo));
        }
      })
      .catch(err => {
        console.error('Remote version check failed:', err);
        // Don't perform local version check on error to prevent unnecessary reloads
      });
  } catch (error) {
    // Log error but don't throw to prevent app crash
    console.error('Version control error:', error);
  }
};

// Perform local version check without remote verification
const performLocalVersionCheck = (storedInfo: string | null, currentInfo: VersionInfo): void => {
  if (!storedInfo) {
    // First visit - store initial version
    localStorage.setItem('version_info', JSON.stringify(currentInfo));
    return;
  }

  const parsedInfo = JSON.parse(storedInfo) as VersionInfo;
  const currentTime = Date.now();
  
  // Check if versions are different and cooldown has passed
  if (
    parsedInfo.version !== APP_VERSION && 
    currentTime - parsedInfo.lastReload > RELOAD_COOLDOWN
  ) {
    performReload(currentInfo);
  }
};

// Fetch the remote version.json file to check for updates or forced refresh
const checkRemoteVersion = async (): Promise<{version: string, buildId: string, forceRefresh: boolean} | null> => {
  try {
    // Add cache busting to prevent cached responses
    const response = await fetch(`/.well-known/version.json?_t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (e) {
    console.warn('Could not fetch remote version:', e);
    return null;
  }
};

// Perform the actual reload with appropriate cache busting
const performReload = (currentInfo: VersionInfo): void => {
  try {
    // Update stored version before reload to prevent loops
    localStorage.setItem('version_info', JSON.stringify(currentInfo));
    
    // Get device info for special handling
    const { isSafari, isAndroid, isIOS } = getMobileDeviceInfo();
    
    // Clear application cache if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Special handling for problematic browsers
    if (isSafari || isAndroid || isIOS) {
      // Force a hard reload with comprehensive cache busting
      const url = window.location.href.split('?')[0];
      const cacheBuster = `_cache=${BUILD_ID}-${Date.now()}`;
      const separator = url.includes('?') ? '&' : '?';
      
      window.location.href = `${url}${separator}${cacheBuster}`;
    } else {
      // Force reload for other browsers 
      window.location.reload();
    }
  } catch (error) {
    console.error('Reload error:', error);
    // Fallback to basic reload
    window.location.reload();
  }
};

export const checkNeedsUpdate = (): boolean => {
  try {
    const storedInfo = localStorage.getItem('version_info');
    if (!storedInfo) return false;

    const parsedInfo = JSON.parse(storedInfo) as VersionInfo;
    return parsedInfo.version !== APP_VERSION || parsedInfo.buildId !== BUILD_ID;
  } catch {
    return false;
  }
};

// Force an immediate update regardless of cooldown
export const forceUpdate = (): void => {
  const currentTime = Date.now();
  
  const currentInfo: VersionInfo = {
    version: APP_VERSION,
    lastReload: currentTime,
    buildId: BUILD_ID
  };
  
  performReload(currentInfo);
};
